import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { base64ToFile } from '@/utils/personalizationStorage';

export type Step = 'info' | 'review' | 'edit';

export interface PersonalizeOption {
  id: string;
  label: string;
  value: string;
  completed: boolean;
}

interface UsePersonalizeFormArgs {
  product: any;
  productData: any;
  inputs: string[];
  showPackagingOption: boolean;
  covers: any[] | undefined;
  t: (key: string) => string;
  locale: string | undefined;
}

export const usePersonalizeForm = ({
  product,
  productData,
  inputs,
  showPackagingOption,
  covers,
  t,
  locale,
}: UsePersonalizeFormArgs) => {
  const router = useRouter();
  const is_edit = productData?.isEdit as string | undefined;
  const userEditedName = useRef(false);

  // --- UUID ---
  const uuid = useMemo(() => {
    if (is_edit === '1') return product?.uuid || productData?.product?.uuid || uuidv4();
    return router.query.uuid ? (router.query.uuid as string) : uuidv4();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable on mount

  // --- Initial gender ---
  const initialGender = useMemo(
    () =>
      is_edit === '1'
        ? productData?.product?.gender === 'Female' ? 'Female' : 'Male'
        : product?.has_female === true ? 'Female' : 'Male',
    [is_edit, product, productData]
  );

  // --- Core form state ---
  const [childName, setChildNameState] = useState<string>(() => {
    if (is_edit === '1') {
      return (
        productData?.product?.name ||
        product?.name ||
        (typeof product?.replace_name === 'string'
          ? product.replace_name
          : locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) ||
        ''
      );
    }
    return '';
  });
  const [inputAge, setInputAge] = useState<string>(() =>
    is_edit === '1'
      ? product?.inputs?.age || productData?.product?.age || ''
      : (router.query.age as string) || ''
  );
  const [childgender, setChildGender] = useState<string>(initialGender);
  const [inputDedication, setInputDedication] = useState<string>(() =>
    is_edit === '1'
      ? product?.dedication || productData?.product?.dedication || ''
      : (router.query.dedication as string) || ''
  );
  const [inputCover, setInputCover] = useState<any>(() =>
    is_edit === '1'
      ? product?.cover_id || productData?.product?.cover_id
      : (router.query.cover as string) || undefined
  );
  const [inputPackaging, setInputPackaging] = useState<boolean | undefined>(() => {
    if (is_edit === '1') {
      if (product?.packaging?.name) return product.packaging.name === 'Packaging';
      if (typeof product?.packaging === 'boolean') return product.packaging;
      return false;
    }
    return false; // component corrects to undefined for non-IL users once showPackagingOption resolves
  });
  const [option, setOption] = useState<number | undefined>(undefined);

  // --- Image state ---
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [imageSelected, setImageSelected] = useState<string>(() =>
    is_edit === '1' &&
    (productData?.dedicationImage || localStorage.getItem(`dedicationImageUrl-${product?.uuid}`))
      ? 'yes'
      : 'no'
  );
  const [imageInEdit, setImageInEdit] = useState<string | null>(null);

  // --- Navigation state ---
  const [step, setStep] = useState<Step>(() => (is_edit === '1' ? 'review' : 'info'));
  const [activeOption, setActiveOption] = useState<string>('name');

  // --- Modal state ---
  const [dedicationModal, setDedicationModal] = useState(false);
  const [coverModal, setCoverModal] = useState(false);
  const [packageModal, setPackageModal] = useState(false);
  const [nameModal, setNameModal] = useState(false);
  const [confirmCart, setConfirmCart] = useState(false);

  // --- Input values map ---
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  // --- Options ---
  const [options, setOptions] = useState<PersonalizeOption[]>([]);

  // --- Loading states ---
  const [loadData, setLoadData] = useState<any>(null);
  const [loadDataLoading, setLoadDataLoading] = useState(false);
  const [showSavedData, setShowSavedData] = useState(true);
  const [isPostLoginFlow, setIsPostLoginFlow] = useState(false);

  // --- Refs for stable callbacks ---
  const childNameRef = useRef(childName);
  const childGenderRef = useRef(childgender);
  useEffect(() => { childNameRef.current = childName; }, [childName]);
  useEffect(() => { childGenderRef.current = childgender; }, [childgender]);
  useEffect(() => { setImageInEdit(croppedImage); }, [croppedImage]);

  // --- Set child name (with user-edit tracking) ---
  const setChildName = useCallback((name: string, isUserEdit = false) => {
    if (isUserEdit) userEditedName.current = true;
    setChildNameState(name);
  }, []);

  // --- Build initial options list ---
  const buildOptions = useCallback(
    (name: string, dedication: string, cover: any, packaging: boolean | undefined): PersonalizeOption[] => {
      const selectedCoverName = (() => {
        if (!covers || !cover) return '';
        const c = covers.find((x: any) => String(x.id) === String(cover));
        if (!c) return '';
        if (typeof c.name === 'string') return c.name;
        return locale === 'ar' ? (c.name?.ar || c.name?.en || '') : (c.name?.en || c.name?.ar || '');
      })();

      const list: PersonalizeOption[] = [
        {
          id: 'child',
          label: t('childLabel'),
          value: name || '',
          completed: is_edit === '1',
        },
        {
          id: 'dedication',
          label: t('dedicationLabel'),
          value: dedication !== '' ? dedication : t('enterDedicationPlaceholderInputDedication'),
          completed: is_edit === '1',
        },
        {
          id: 'cover',
          label: t('coverLabel'),
          value: selectedCoverName || t('chooseCover'),
          completed: is_edit === '1',
        },
      ];

      if ((is_edit === '1' && product?.packaging) || showPackagingOption) {
        list.push({
          id: 'packaging',
          label: t('packagingLabel'),
          value:
            is_edit === '1' && product?.packaging
              ? t('withPackaging')
              : packaging === true
              ? t('withPackaging')
              : t('withoutPackaging'),
          completed: is_edit === '1',
        });
      }

      return list;
    },
    [covers, locale, t, is_edit, product, showPackagingOption]
  );

  // Initialise options
  useEffect(() => {
    setOptions(buildOptions(childName, inputDedication, inputCover, inputPackaging));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [covers]); // only on mount / cover data load

  // Sync option values when form changes
  useEffect(() => {
    const selectedCoverName = (() => {
      if (!covers || !inputCover) return '';
      const c = covers.find((x: any) => String(x.id) === String(inputCover));
      if (!c) return '';
      if (typeof c.name === 'string') return c.name;
      return locale === 'ar' ? (c.name?.ar || c.name?.en || '') : (c.name?.en || c.name?.ar || '');
    })();

    setOptions((prev) =>
      prev.map((opt) => {
        switch (opt.id) {
          case 'child':
            return { ...opt, value: childName };
          case 'dedication':
            return { ...opt, value: inputDedication !== '' ? inputDedication : t('enterDedicationPlaceholderInputDedication') };
          case 'cover':
            return { ...opt, value: selectedCoverName || t('chooseCover') };
          case 'packaging':
            return {
              ...opt,
              value:
                is_edit === '1' && product?.packaging
                  ? t('withPackaging')
                  : inputPackaging === true
                  ? t('withPackaging')
                  : t('withoutPackaging'),
            };
          default:
            return opt;
        }
      })
    );
  }, [childName, inputDedication, inputCover, inputPackaging, covers, locale, t, is_edit, product]);

  const markOptionCompleted = useCallback((id: string, completed: boolean) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, completed } : opt)));
  }, []);

  // --- inputValues sync ---
  useEffect(() => {
    const urlInputs = (() => {
      if (router.query.inputs) {
        try { return JSON.parse(router.query.inputs as string); } catch { return {}; }
      }
      return {};
    })();

    const vals: Record<string, string> = {};
    Object.keys(inputs).forEach((key) => {
      vals[key] = urlInputs[key] || '';
    });

    if ('name' in vals) {
      if (userEditedName.current) {
        vals.name = childName;
      } else {
        const urlName = router.query.name as string | undefined;
        const defaultName = locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en;
        vals.name = urlName && urlName !== defaultName ? urlName : childName;
      }
    }
    setInputValues(vals);
  }, [inputs, childName, locale, product?.replace_name, router.query.name, router.query.inputs]);

  // --- Non-edit mode: sync childName from URL ---
  useEffect(() => {
    if (is_edit === '1' || !product || !locale) return;
    if (userEditedName.current) return;

    const defaultName = locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en;
    const urlName = router.query.name as string | undefined;
    setChildNameState(urlName && urlName !== defaultName ? urlName : '');
  }, [is_edit, product, router.query.name, locale]);

  // --- Edit mode: mark all options completed ---
  useEffect(() => {
    if (is_edit === '1') {
      markOptionCompleted('child', true);
      markOptionCompleted('gender', true);
      markOptionCompleted('name', true);
      markOptionCompleted('dedication', true);
      markOptionCompleted('cover', true);
      if (showPackagingOption) markOptionCompleted('packaging', true);
    }
  }, [is_edit, showPackagingOption, markOptionCompleted]);

  // --- Load saved data ---
  useEffect(() => {
    if (isPostLoginFlow || is_edit === '1' || loadData === null) return;
    setLoadDataLoading(true);

    const run = async () => {
      const loaded = loadData as any;
      const name = loaded.childName || '';
      const age = loaded.inputAge || '';
      const dedication = loaded.inputDedication || '';
      const cover = loaded.inputCover || undefined;
      const packaging = showPackagingOption ? (loaded.inputPackaging ?? false) : undefined;

      setChildNameState(name);
      userEditedName.current = true;
      setInputAge(age);
      setChildGender(loaded.childgender === initialGender ? loaded.childgender : initialGender);
      setInputDedication(dedication);
      setInputCover(cover);
      if (showPackagingOption) setInputPackaging(packaging);

      if (loaded.croppedImageBase64) {
        setCroppedImage(loaded.croppedImageBase64);
        const file = await base64ToFile(loaded.croppedImageBase64, 'dedication.jpg', 'image/jpeg');
        if (file) {
          setCroppedFile(file);
          setImageSelected('yes');
        } else {
          setImageSelected('no');
        }
      } else {
        setImageSelected('no');
      }

      if (name) {
        markOptionCompleted('child', true);
        markOptionCompleted('name', true);
      }
      if (dedication) markOptionCompleted('dedication', true);
      if (cover) markOptionCompleted('cover', true);
      if (showPackagingOption && packaging) markOptionCompleted('packaging', true);

      setLoadDataLoading(false);
    };

    run();
  }, [loadData, is_edit, showPackagingOption, isPostLoginFlow, initialGender, markOptionCompleted]);

  // --- Reset post-login on info step ---
  useEffect(() => {
    if (step === 'info') setIsPostLoginFlow(false);
  }, [step]);

  // --- UpdateValue (used by inputs) ---
  const updateValue = useCallback((key: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
    if (key === 'name') {
      userEditedName.current = true;
      setChildNameState(value);
    }
    if (key === 'age') setInputAge(value);
  }, []);

  // --- Reset form ---
  const resetForm = useCallback(() => {
    setChildNameState('');
    setInputAge('');
    setInputDedication('');
    setInputCover(undefined);
    setInputPackaging(showPackagingOption ? false : undefined);
    setCroppedImage(null);
    setCroppedFile(null);
    setImageSelected('no');
    userEditedName.current = true;
    const vals: Record<string, string> = {};
    Object.keys(inputs).forEach((k) => { vals[k] = ''; });
    setInputValues(vals);
    markOptionCompleted('child', false);
    markOptionCompleted('name', false);
    markOptionCompleted('gender', false);
    markOptionCompleted('dedication', false);
    markOptionCompleted('cover', false);
    if (showPackagingOption) markOptionCompleted('packaging', false);
  }, [showPackagingOption, inputs, markOptionCompleted]);

  const handleAddNew = useCallback(() => {
    resetForm();
    setLoadData(null);
    setShowSavedData(false);
  }, [resetForm]);

  // --- Derived values ---
  const hasValidUrlName = useMemo(() => {
    if (is_edit === '1') return false;
    const urlName = router.query.name as string | undefined;
    if (!urlName) return false;
    const defaultName = locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en;
    return urlName !== defaultName;
  }, [is_edit, router.query.name, locale, product?.replace_name]);

  const customInputs = useMemo(() => ({
    name: childName || (is_edit === '1' ? productData?.product?.replace_name || '' : ''),
    first_name: '',
    age: inputAge || (is_edit === '1' ? productData?.product?.age || '' : ''),
    init: childName?.charAt(0) || '',
    dedication: inputDedication || (is_edit === '1' ? productData?.product?.dedication || '' : ''),
  }), [childName, inputAge, inputDedication, productData, is_edit]);

  return {
    // IDs
    uuid,
    is_edit,
    // Form values
    childName,
    setChildName,
    inputAge,
    setInputAge,
    childgender,
    setChildGender,
    inputDedication,
    setInputDedication,
    inputCover,
    setInputCover,
    inputPackaging,
    setInputPackaging,
    // Image
    croppedImage,
    setCroppedImage,
    croppedFile,
    setCroppedFile,
    imageSelected,
    setImageSelected,
    imageInEdit,
    // Navigation
    step,
    setStep,
    activeOption,
    setActiveOption,
    // Modals
    dedicationModal,
    setDedicationModal,
    coverModal,
    setCoverModal,
    packageModal,
    setPackageModal,
    nameModal,
    setNameModal,
    confirmCart,
    setConfirmCart,
    // Options
    options,
    markOptionCompleted,
    // Input values
    inputValues,
    updateValue,
    customInputs,
    // Dedication option selection
    option,
    setOption,
    // Load saved data
    loadData,
    setLoadData,
    loadDataLoading,
    showSavedData,
    setShowSavedData,
    isPostLoginFlow,
    // Helpers
    hasValidUrlName,
    initialGender,
    resetForm,
    handleAddNew,
    childNameRef,
    childGenderRef,
    userEditedName,
  };
};
